import { PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/solid";
import { useEffect, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import toast from "react-hot-toast";
import { getBgColorFromDate } from "../../../helpers/getBgColorFromDate";
import { deleteClientServiceSubscriptionTaskChecklistItem } from "../../../services/clientServiceSubscriptionTaskChecklistItem/deleteClientServiceSubscriptionChecklistItem";
import { getClientServiceSubscriptionTaskChecklistItemByTaskId } from "../../../services/clientServiceSubscriptionTaskChecklistItem/getClientServiceSubscriptionTaskChecklistItemByTaskId";
import { updateClientServiceSubscriptionTaskChecklistItem } from "../../../services/clientServiceSubscriptionTaskChecklistItem/updateClientServiceSubscriptionTaskChecklistItem";
import { updateClientServiceSubscriptionTaskChecklistItemCheck } from "../../../services/clientServiceSubscriptionTaskChecklistItem/updateClientServiceSubscriptionTaskChecklistItemCheck";
import { getEmployees } from "../../../services/getEmployees";
import DeleteModal from "../../../utils/DeleteModal";
import Badge from "../../Badge";
import ChecklistItemsList from "../../clientServiceSubscription/tasks/checklistItems/ChecklistItemsList";
import ConfirmationModal from "../../Modals/ConfirmationModal";
import AddNewClientServiceSubscriptionTaskChecklistItem from "./addNewChecklistItem/AddNewClientServiceSubscriptionTaskChecklistItem";
import EditClientServiceSubscriptionTaskChecklistItem from "./updateChecklistItem/EditClientServiceSubscriptionTaskChecklistItem";
import LoadingOverlay from "../../LoadingOverlay";
import { term } from "../../../terms";

const ORDER_STEP = 1000;

const ClientServiceSubscriptionTaskChecklistItemTableBody = ({
    task,
    loggedInEmployee,
    onUpdateAllChecklistItems,
    isServiceStatusInProgress,
    getNewJobs,
    updateClientService,
    shouldResetChecklist,
    setShouldResetChecklist,
    canReadSteps,
    canWriteSteps,
    canDeleteSteps,
}) => {
    const [checklistItems, setChecklistItems] = useState([]);
    const [updateChecklistItem, setUpdateChecklistItem] = useState({});
    const [openEditChecklistItem, setOpenEditChecklistItem] = useState(false);
    const [openAddNewCheckListItem, setOpenAddNewChecklistItem] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [showDialog, setShowDialog] = useState(false);
    const [itemToChange, setItemToChange] = useState({});
    const [isChecked, setIsChecked] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [checklistItemToDelete, setChecklistItemToDelete] = useState(null);
    const [isBulkUncheck, setIsBulkUncheck] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [dragResult, setDragResult] = useState(null);
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const [overlayMessage, setOverlayMessage] = useState("Updating, please wait...");
    const [preModalChecklist, setPreModalChecklist] = useState(null);
    const suppressNextChecklistLoaderRef = useRef(null);
    const isAdmin = loggedInEmployee.role === 1;
    const canEditCustomItem = isAdmin && canWriteSteps;
    const canDeleteCustomItem = isAdmin && canDeleteSteps;

    useEffect(() => {
        if (!canReadSteps || !canWriteSteps || !shouldResetChecklist) return;

        (async () => {
            try {
                if (preModalChecklist) {
                    setOverlayMessage("Reverting checklist...");
                    setIsOverlayOpen(true);

                    const desiredMap = new Map(
                        preModalChecklist.map((i) => [i.id, i.isChecked]),
                    );

                    let updatedLocal = [...checklistItems];

                    for (const item of checklistItems) {
                        const desired = desiredMap.get(item.id);
                        if (desired === undefined) continue;
                        if (item.isChecked === desired) continue;

                        const resp =
                            await updateClientServiceSubscriptionTaskChecklistItemCheck(
                                item.id,
                                desired,
                            );

                        if (resp.success) {
                            updatedLocal = updatedLocal.map((el) =>
                                el.id === item.id
                                    ? { ...el, isChecked: desired }
                                    : el,
                            );
                        }
                    }

                    setChecklistItems(updatedLocal);
                } else {
                    await getNewChecklistItems(false);
                }
            } finally {
                setPreModalChecklist(null);
                setIsOverlayOpen(false);
                setShouldResetChecklist(false);
            }
        })();
    }, [shouldResetChecklist, canReadSteps, canWriteSteps]);

    const onHandleClickAddNewChecklistItem = () => {
        if (!canWriteSteps) return;
        setOpenAddNewChecklistItem(true);
    };

    const linkify = (text) => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        return text.split(urlRegex).map((part, i) =>
            urlRegex.test(part) ? (
                <a
                    key={i}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-words"
                >
                    {part}
                </a>
            ) : (
                <span key={i}>{part}</span>
            ),
        );
    };

    const UpdateAllChecklistItems = async (taskId, checkValue) => {
        if (!canWriteSteps) return;

        if (!isServiceStatusInProgress && task.status !== 2) {
            toast.error("Start service before checking or unchecking items");
            return;
        }

        if (task.status === 0) {
            toast.error(
                `Start the ${term("step_singular")} before checking or unchecking items.`,
            );
            return;
        }

        const alreadyAllInDesiredState = checklistItems.every(
            (item) => item.isChecked === checkValue,
        );
        if (alreadyAllInDesiredState) return;

        if (checkValue && preModalChecklist === null) {
            setPreModalChecklist([...checklistItems]);
        }

        if (checkValue === false && task.status === 2) {
            setShowDialog(true);
            setItemToChange(null);
            setIsChecked(false);
            setIsBulkUncheck(true);
            return;
        }

        await performBulkCheckUpdate(checkValue);
    };

    const performBulkCheckUpdate = async (checkValue) => {
        if (!canWriteSteps) return;

        setOverlayMessage(
            checkValue ? "Checking all items..." : "Unchecking all items...",
        );
        setIsOverlayOpen(true);

        try {
            let updatedItems = [...checklistItems];

            for (const item of checklistItems) {
                if (item.isChecked === checkValue) continue;

                const response =
                    await updateClientServiceSubscriptionTaskChecklistItemCheck(
                        item.id,
                        checkValue,
                    );

                if (response.success) {
                    updatedItems = updatedItems.map((el) =>
                        el.id === item.id ? { ...el, isChecked: checkValue } : el,
                    );
                }
            }

            setChecklistItems(updatedItems);
            getNewJobs(task.order);
            updateClientService();

            if (checkValue && updatedItems.every((i) => i.isChecked)) {
                const lastCheckedItem = checklistItems[checklistItems.length - 1];
                onUpdateAllChecklistItems(task, lastCheckedItem);
            }

            toast.success(
                `All checklist items have been ${
                    checkValue ? "checked" : "unchecked"
                }.`,
            );
        } finally {
            setIsOverlayOpen(false);
        }
    };

    const onChangeItemInstructions = async (item, isCheckedValue) => {
        if (!canWriteSteps) return;

        const newCheckListItems = checklistItems.map((el) =>
            el.id === item.id ? { ...el, isChecked: isCheckedValue } : el,
        );

        const allChecked = newCheckListItems.every((i) => i.isChecked);

        // Manual last-item check → auto-complete job but without forcing renew flow
        if (allChecked) {
            // If this was CHECK ALL, preModalChecklist exists → bypassAutoUpdate = true
            const triggeredByCheckAll = preModalChecklist !== null;

            onUpdateAllChecklistItems(task, item, {
                bypassAutoUpdate: triggeredByCheckAll,
            });

            return;
        }

        const response = await updateClientServiceSubscriptionTaskChecklistItemCheck(
            item.id,
            isCheckedValue,
        );

        if (response.success) {
            setChecklistItems(newCheckListItems);
            suppressNextChecklistLoaderRef.current = task.id;
            getNewJobs(task.order);
            updateClientService();
        }
    };

    const onChangeItemCheckV2 = async (item, isCheckedValue) => {
        if (!canWriteSteps) return;

        if (!isServiceStatusInProgress && task.status !== 2) {
            toast.error("Start service before checking or unchecking items");
            return;
        }

        if (task.status === 0) {
            toast.error(
                `Start the ${term("step_singular")} before checking or unchecking an item.`,
            );
            return;
        }

        if (!isCheckedValue && task.status === 2) {
            setShowDialog(true);
            setItemToChange(item);
            setIsChecked(isCheckedValue);
            return;
        }

        onChangeItemInstructions(item, isCheckedValue);
    };

    const onHandleClickEditItem = (item) => {
        if (!canEditCustomItem) return;
        setUpdateChecklistItem(item);
        setOpenEditChecklistItem(true);
    };

    const getChecklistItemsWithBgColor = (items) =>
        items.map((item) => ({
            ...item,
            bgColor: getBgColorFromDate(item.dueDate),
        }));

    const getNewChecklistItems = async (showLoader = true) => {
        if (!canReadSteps) return;

        if (showLoader) {
            setOverlayMessage("Loading checklist...");
            setIsOverlayOpen(true);
        }

        try {
            const result =
                await getClientServiceSubscriptionTaskChecklistItemByTaskId(task.id);
            const sortedItems = [...(result.data.checklistItemsResponse || [])].sort(
                (a, b) => Number(a.order) - Number(b.order),
            );
            const checklistWithColor = getChecklistItemsWithBgColor(sortedItems);
            setChecklistItems(checklistWithColor);
        } finally {
            if (showLoader) {
                setIsOverlayOpen(false);
            }
        }
    };

    const getNewEmployees = async () => {
        if (!canReadSteps || !canWriteSteps) return;
        const result = await getEmployees();
        setEmployees(result.data.employees);
    };

    useEffect(() => {
        if (!canReadSteps) return;

        const shouldSuppressChecklistLoader =
            suppressNextChecklistLoaderRef.current === task.id;
        suppressNextChecklistLoaderRef.current = null;

        getNewChecklistItems(!shouldSuppressChecklistLoader);
        getNewEmployees();
    }, [task, canReadSteps, canWriteSteps]);

    const reorder = (list, startIndex, endIndex) => {
        const result = [...list];
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const getNewOrder = (items, destinationIndex) => {
        const previousItem = items[destinationIndex - 1];
        const nextItem = items[destinationIndex + 1];

        if (!previousItem && nextItem) {
            return Number(nextItem.order) - ORDER_STEP;
        }

        if (previousItem && !nextItem) {
            return Number(previousItem.order) + ORDER_STEP;
        }

        if (previousItem && nextItem) {
            return (Number(previousItem.order) + Number(nextItem.order)) / 2;
        }

        return ORDER_STEP;
    };

    const onDragEnd = (result) => {
        if (!canWriteSteps) return;
        if (!result.destination) return;

        const sourceItem = checklistItems[result.source.index];
        if (!sourceItem?.isCustom) return;

        if (!isAdmin) {
            toast.error("You are not allowed to do this action.");
            return;
        }
        if (!isServiceStatusInProgress) {
            toast.error("Must START service before reordering checklist items.");
            return;
        }

        setDragResult(result);
        setIsConfirmModalOpen(true);
    };

    const confirmReorder = async () => {
        if (!canEditCustomItem || !dragResult) return;

        const { source, destination } = dragResult;

        if (!destination || source.index === destination.index) {
            setIsConfirmModalOpen(false);
            setDragResult(null);
            return;
        }

        const reordered = reorder(checklistItems, source.index, destination.index);
        const movedItem = reordered[destination.index];
        const newOrder = getNewOrder(reordered, destination.index);
        const updatedMovedItem = {
            ...movedItem,
            order: newOrder,
        };
        const updatedChecklistItems = reordered.map((item) =>
            item.id === movedItem.id ? updatedMovedItem : item,
        );

        setChecklistItems(updatedChecklistItems);
        setOverlayMessage("Reordering items...");
        setIsOverlayOpen(true);

        try {
            const response =
                await updateClientServiceSubscriptionTaskChecklistItem(
                    updatedMovedItem,
                );

            if (response.data?.success) {
                toast.success(
                    response.data.message ||
                        "Checklist item order updated successfully.",
                );
            } else {
                toast.error(
                    response.data?.message ||
                        "Could not update checklist item order.",
                );
            }

            await getNewChecklistItems(false);
        } catch (err) {
            console.error("Error updating checklist item order:", err);
            toast.error("Could not update checklist item order.");
            await getNewChecklistItems(false);
        } finally {
            setIsOverlayOpen(false);
            setIsConfirmModalOpen(false);
            setDragResult(null);
        }
    };

    const deleteChecklistItem = (id) => {
        if (!canDeleteCustomItem) return;
        setChecklistItemToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const removeChecklistItem = async () => {
        if (!canDeleteCustomItem) return;

        setOverlayMessage("Deleting item...");
        setIsOverlayOpen(true);

        try {
            const response =
                await deleteClientServiceSubscriptionTaskChecklistItem(
                    checklistItemToDelete,
                );

            if (response.success) {
                await getNewChecklistItems(false);
                toast.success(response.data.message);
            }
        } finally {
            setIsDeleteModalOpen(false);
            setIsOverlayOpen(false);
        }
    };

    if (!canReadSteps) return null;

    return (
        <>
            <LoadingOverlay isLoading={isOverlayOpen} message={overlayMessage} />

            <DeleteModal
                isModalOpen={isConfirmModalOpen}
                setIsModalOpen={setIsConfirmModalOpen}
                title="Are you sure you want to move this item?"
                buttonText="Move"
                onRemove={confirmReorder}
            />

            <ConfirmationModal
                title="Step status will change!"
                description="Are you sure you want to uncheck this item?"
                showDialog={showDialog}
                onCancel={() => {
                    setShowDialog(false);
                    setIsBulkUncheck(false);
                    setItemToChange({});
                }}
                onConfirm={() => {
                    setShowDialog(false);
                    if (isBulkUncheck) {
                        performBulkCheckUpdate(false);
                        setIsBulkUncheck(false);
                    } else {
                        onChangeItemInstructions(itemToChange, isChecked);
                    }
                }}
            />

            {canWriteSteps && (
                <AddNewClientServiceSubscriptionTaskChecklistItem
                    open={openAddNewCheckListItem}
                    setOpen={setOpenAddNewChecklistItem}
                    taskId={task.id}
                    getNewChecklistItems={getNewChecklistItems}
                    loggedInEmployee={loggedInEmployee}
                    employees={employees}
                    updateClientService={updateClientService}
                    getNewJobs={getNewJobs}
                />
            )}

            {canEditCustomItem && (
                <EditClientServiceSubscriptionTaskChecklistItem
                    updateChecklistItem={updateChecklistItem}
                    setUpdateChecklistItem={setUpdateChecklistItem}
                    open={openEditChecklistItem}
                    setOpen={setOpenEditChecklistItem}
                    updateChecklistItems={(item) => {
                        const updated = checklistItems.map((el) =>
                            el.id === item.id
                                ? {
                                      ...item,
                                      bgColor: getBgColorFromDate(item.dueDate),
                                  }
                                : el,
                        );
                        setChecklistItems(updated);
                    }}
                    loggedInEmployee={loggedInEmployee}
                    employees={employees}
                    getNewJobs={getNewJobs}
                />
            )}

            <DeleteModal
                isModalOpen={isDeleteModalOpen}
                setIsModalOpen={setIsDeleteModalOpen}
                onRemove={removeChecklistItem}
            />

            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                    {(provided) => (
                        <tbody
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="text-sm divide-y divide-slate-200"
                        >
                            <tr className="py-3">
                                <td colSpan={5} className="px-2 py-3 text-left">
                                    {canWriteSteps && (
                                        <button
                                            className="inline-flex items-center h-10 text-white bg-teal-500 hover:bg-teal-600 btn"
                                            onClick={
                                                onHandleClickAddNewChecklistItem
                                            }
                                        >
                                            <PlusIcon className="w-4 h-4" />
                                            <span className="ml-2">
                                                Add custom checklist item
                                            </span>
                                        </button>
                                    )}
                                </td>
                            </tr>

                            <tr className="text-xs font-semibold uppercase bg-slate-50 border-y border-slate-200">
                                <td className="px-2 py-3">
                                    Status
                                    {isAdmin && canWriteSteps && (
                                        <div className="flex items-center gap-2 my-2">
                                            <input
                                                type="checkbox"
                                                ref={(el) => {
                                                    if (!el) return;
                                                    const total =
                                                        checklistItems.length;
                                                    const checked =
                                                        checklistItems.filter(
                                                            (i) => i.isChecked,
                                                        ).length;
                                                    el.indeterminate =
                                                        checked > 0 &&
                                                        checked < total;
                                                }}
                                                checked={
                                                    checklistItems.length > 0 &&
                                                    checklistItems.every(
                                                        (i) => i.isChecked,
                                                    )
                                                }
                                                onChange={() => {
                                                    const shouldCheck =
                                                        !checklistItems.every(
                                                            (i) => i.isChecked,
                                                        );
                                                    UpdateAllChecklistItems(
                                                        task.id,
                                                        shouldCheck,
                                                    );
                                                }}
                                                className="w-5 h-5 cursor-pointer accent-teal-600"
                                            />
                                            <label className="cursor-pointer select-none">
                                                {checklistItems.every(
                                                    (i) => i.isChecked,
                                                )
                                                    ? "Uncheck All"
                                                    : "Check All"}
                                            </label>
                                        </div>
                                    )}
                                </td>

                                <td className="px-2 py-3 text-left">Description</td>
                                <td className="px-2 py-3 text-left">Links</td>
                                <td className="px-2 py-3 text-center">Edit</td>
                                <td className="px-2 py-3 text-center">Delete</td>
                            </tr>

                            <tr>
                                <td
                                    colSpan={5}
                                    className="px-2 pt-2 pb-1 text-xs text-slate-500"
                                >
                                    Tip: custom items can be dragged to reorder.
                                </td>
                            </tr>

                            {checklistItems.map((item, index) => {
                                const isCustom = item.isCustom;

                                return (
                                    <Draggable
                                        key={item.id}
                                        draggableId={item.id}
                                        index={index}
                                        isDragDisabled={
                                            !isCustom || !canEditCustomItem
                                        }
                                    >
                                        {(provided) => (
                                            <tr
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                className={`transition-colors ${
                                                    isCustom
                                                        ? "group hover:bg-teal-50 border-l-4 border-transparent hover:border-teal-500"
                                                        : ""
                                                }`}
                                            >
                                                <td className="px-2 py-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={item.isChecked}
                                                        disabled={!canWriteSteps}
                                                        onChange={(e) =>
                                                            onChangeItemCheckV2(
                                                                item,
                                                                e.target.checked,
                                                            )
                                                        }
                                                        className="w-5 h-5 cursor-pointer accent-teal-600"
                                                    />
                                                </td>

                                                <td className="px-2 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            className={`mr-1 inline-flex items-center justify-center w-7 h-7 rounded-md ${
                                                                isCustom
                                                                    ? "cursor-grab active:cursor-grabbing hover:bg-slate-100 focus:ring-2 focus:ring-teal-500"
                                                                    : "cursor-not-allowed opacity-40"
                                                            }`}
                                                            {...(isCustom
                                                                ? provided.dragHandleProps
                                                                : {})}
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                className="w-5 h-5"
                                                                viewBox="0 0 24 24"
                                                                fill="currentColor"
                                                            >
                                                                <path d="M4 7h16v2H4zM4 11h16v2H4zM4 15h16v2H4z" />
                                                            </svg>
                                                        </button>

                                                        <div className="px-2 py-2 text-sm font-semibold break-words">
                                                            {linkify(
                                                                item.description,
                                                            )}{" "}
                                                            {item.isCustom && (
                                                                <Badge
                                                                    bgColor="teal"
                                                                    label="Custom"
                                                                />
                                                            )}
                                                            {item.isPermanent && (
                                                                <Badge
                                                                    bgColor="gray"
                                                                    label="Permanent"
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>

                                                <td className="px-2 py-2 text-sm break-all">
                                                    <ChecklistItemsList
                                                        links={item.links}
                                                    />
                                                </td>

                                                <td className="px-2 py-3 text-center">
                                                    {isCustom &&
                                                    canEditCustomItem ? (
                                                        <button
                                                            onClick={() =>
                                                                onHandleClickEditItem(
                                                                    item,
                                                                )
                                                            }
                                                            className="text-slate-400 hover:text-slate-500"
                                                        >
                                                            <PencilSquareIcon className="w-6 h-6" />
                                                        </button>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </td>

                                                <td className="px-2 py-3 text-center">
                                                    {isCustom &&
                                                    canDeleteCustomItem ? (
                                                        <button
                                                            onClick={() =>
                                                                deleteChecklistItem(
                                                                    item.id,
                                                                )
                                                            }
                                                            className="text-slate-400 hover:text-slate-500"
                                                        >
                                                            <TrashIcon className="w-6 h-6" />
                                                        </button>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </Draggable>
                                );
                            })}

                            {provided.placeholder}
                        </tbody>
                    )}
                </Droppable>
            </DragDropContext>
        </>
    );
};

export default ClientServiceSubscriptionTaskChecklistItemTableBody;
